import { useEffect, useState } from "react";
import { singletonHook } from "react-singleton-hook";

import { ReduxAuth } from "./types";
import { datadogIdentify } from "./useDatadogIdentify";

//NOTE: window.getAuthData is expected to be instantiated by rental-sales-components repo.
// https://gitlab.internal.equipmentshare.com/rentals/rental-web-components/-/blob/main/doc/auth-data-sharing.md

declare global {
  interface Window {
    getAuthData?: () => ReduxAuth;
  }
}

const INITIAL_STATE: ReduxAuth = {
  isLoading: true,
  isAuthenticated: false,
  identity: undefined,
};

const useReduxAuthImpl = (): ReduxAuth | undefined => {
  const [isLoading, setIsLoading] = useState<ReduxAuth["isLoading"]>(
    INITIAL_STATE.isLoading
  );
  const [isAuthenticated, setIsAuthenticated] = useState<
    ReduxAuth["isAuthenticated"]
  >(INITIAL_STATE.isAuthenticated);
  const [identity, setIdentity] = useState<ReduxAuth["identity"] | undefined>(
    INITIAL_STATE.identity
  );

  useEffect(() => {
    const getAuthData = (retry: number) => {
      if (isLoading) {
        const authData = window.getAuthData ? window.getAuthData() : undefined;

        if (authData && authData.isLoading !== isLoading) {
          setIsLoading(authData?.isLoading);
        }

        if (authData?.isAuthenticated && authData?.identity) {
          setIsAuthenticated(authData.isAuthenticated);
          setIdentity(authData.identity);
          datadogIdentify(authData.identity);
        } else if (retry < 10) {
          retry++;
          setTimeout(() => getAuthData(retry), retry * 100);
        }
      }
    };

    getAuthData(1);
  }, []);

  return {
    isLoading,
    isAuthenticated,
    identity,
  };
};

export const useReduxAuth = singletonHook(INITIAL_STATE, useReduxAuthImpl);

export const setReduxAuth = (reduxAuth: ReduxAuth) => {
  window.getAuthData = () => reduxAuth;
};
